import {useConfig} from '@aboutyou/config';
import {Image as ImageType} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/image.types';
import {
  BrandLogo,
  COLORS,
  FONT_SIZES,
  Image,
  ImageRatio,
  UnstyledLinkLinaria,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';

interface HeroContentEmptyProps {
  className?: string;
  style?: React.CSSProperties;
  image: ImageType;
  brandLogo?: ImageType;
  brandName: string;
  cta: {label: string; url: string};
  onClick: VoidFunction;
}

export const HeroContentEmpty: React.FC<HeroContentEmptyProps> = ({
  image,
  brandLogo,
  brandName,
  cta,
  onClick,
}) => {
  const {device} = useConfig();

  const defaultAspetRatio = device === 'desktop' ? '2.1:1' : '1:1.33';
  const imageRatio: ImageRatio = image.aspectRatio
    ? `${image.aspectRatio.width}:${image.aspectRatio.height}`
    : defaultAspetRatio;

  return (
    <Wrapper data-testid="heroContentEmpty" onClick={onClick}>
      {image.url && (
        <Background
          ratio={imageRatio}
          src={image.url}
          breakpoints={[]}
          loading="eager"
          importance="high"
        />
      )}
      <Content>
        <StyledBrandLogo
          name={brandName}
          image={brandLogo}
          maxWidth={device === 'desktop' ? '20%' : '50%'}
          showTooltip={false}
        />
        <CTA to={cta.url}>{cta.label}</CTA>
      </Content>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.4);
  }
`;

const Background = styled(Image)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
`;

const StyledBrandLogo = styled(BrandLogo)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  color: ${COLORS['Text.White']};
  font-size: ${FONT_SIZES.px32};

  ${tabletAndUpLinaria(`
    font-size: ${FONT_SIZES.px54};
  `)}
`;

const Content = styled.div`
  position: absolute;
  top: 0%;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 1;

  ${tabletAndUpLinaria('gap: 24px;')}
`;

const CTA = styled(UnstyledLinkLinaria)`
  text-transform: uppercase;
  border-bottom: 1px solid ${COLORS.White};
  font-size: ${FONT_SIZES.px12};
  &&& {
    color: ${COLORS.White};
  }

  ${tabletAndUpLinaria(`
    font-size: 16px;
  `)}
`;
